import { memo, useEffect, useMemo, useState, useContext } from "react";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import InfoIcon from "@mui/icons-material/Info";
import {
	Box,
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { MeasurementsDaily } from "@/model/Application/Settings/SettingsResponseTypes";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);
interface Props {
	measurement: MeasurementsDaily;
	handleFieldChange: (
		id: number,
		labAnalyzeId: number,
		field: keyof MeasurementsDaily,
		value: MeasurementsDaily[keyof MeasurementsDaily],
	) => void;
	handleDialogOpen: (labMeasurementId: number, labAnalyzeId: number) => void;
}

/**
 * TagMeasurement component is responsible for rendering a measurement tag with various fields and controls.
 * It displays measurement details and allows the user to edit certain fields based on the measurement properties.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.measurement - The measurement data object.
 * @param {Function} props.handleFieldChange - The function to handle changes in the measurement fields.
 * @param {Function} props.handleDialogOpen - The function to handle opening a dialog for more information.
 *
 * @returns {JSX.Element} The rendered TagMeasurement component.
 *
 * @example
 * <TagMeasurement
 *   measurement={measurement}
 *   handleFieldChange={handleFieldChange}
 *   handleDialogOpen={handleDialogOpen}
 * />
 *
 * @description
 * The component uses the `useTime` hook to get the current timezone and initializes the `initialDate` state
 * based on the measurement's `AnalyseType` and `TestDate` properties. It updates the `initialDate` state
 * whenever the `measurement.AnalyseType`, `measurement.TestDate`, or `timezone` changes.
 *
 * The component renders a grid with the following fields:
 * - Parameter Name: Displays the name of the measurement parameter.
 * - Measurement: A text field for entering the measurement value. The background color changes based on whether the value is within limits.
 * - Comment: A text field for adding a description or comment.
 * - Test Date & Time: A datetime-local field for entering the test date and time. It is read-only if `AnalyseType` is 1.
 * - More Info: An icon button that opens a dialog with more information about the measurement.
 */
const TagMeasurement = ({
	measurement,
	handleFieldChange,
	handleDialogOpen,
}: Props) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();
	const [initialDate, setInitialDate] = useState<string>(
		dayjs().tz(timezone).format("YYYY-MM-DDTHH:mm"),
	);
	useEffect(() => {
		let calculatedDate;

		if (measurement.AnalyseTime === 1) {
			// AnalyseTime case: default to "yesterday at 23:59:00"
			calculatedDate = dayjs()
				.tz(timezone)
				.subtract(1, "day")
				.hour(23)
				.minute(59)
				.second(0)
				.millisecond(0);
		} else if (
			!measurement.TestDate ||
			dayjs(measurement.TestDate).isBefore("2000-10-10")
		) {
			// Placeholder or invalid date: use the current date/time
			calculatedDate = dayjs().tz(timezone);
		} else {
			// Valid TestDate: parse and convert to the current timezone
			calculatedDate = dayjs.utc(measurement.TestDate).tz(timezone);
		}

		setInitialDate(calculatedDate.format("YYYY-MM-DDTHH:mm"));
	}, [measurement.AnalyseTime, measurement.TestDate, timezone]);

	// Memoize within limits check
	const isWithinLimits = useMemo(() => {
		try {
			const parsedValue = parseFloat(
				String(measurement.Measurement || "").replace(",", "."),
			);
			if (isNaN(parsedValue)) return false;
			return (
				parsedValue >= measurement.MinLimit &&
				parsedValue <= measurement.MaxLimit
			);
		} catch {
			return false;
		}
	}, [measurement.Measurement, measurement.MinLimit, measurement.MaxLimit]);
	return (
		<Grid
			container
			spacing={1.5}
			key={measurement.LabMeasurementId}
			alignItems="flex-end"
			sx={{
				mb: 1,
				px: 1.5,
				pt: 1.5,
				pb: 1.5,
				borderBottom: 1,
				borderColor: "divider",
				borderRadius: 2,
				bgcolor: "background.paper",
				"&:hover": {
					backgroundColor: "action.hover",
				},
			}}
		>
			<Grid item xs={12} sm={4} md={3}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: 600,
						color: "text.primary",
						mb: 0.5,
					}}
				>
					{measurement.ParameterName}
				</Typography>
			</Grid>

			<Grid item xs={12} sm={3} md={2}>
				<TextField
					fullWidth
					value={measurement.Measurement || ""}
					label={
						<Translate String="LabDaily.MeasurementLabel">
							Measurement
						</Translate>
					}
					type="text"
					variant="outlined"
					size="small"
					onChange={(e) => {
						const value = e.target.value;
						// Allow: optional "-", digits, optional single dot or comma as decimal separator
						const validInput = /^-?\d*[.,]?\d*$/.test(value);
						if (!validInput) {
							return;
						}
						try {
							handleFieldChange(
								measurement.LabMeasurementId,
								measurement.LabAnalyzeId,
								"Measurement",
								value,
							);
						} catch (error) {
							console.error("Error updating measurement:", error);
						}
					}}
					sx={(theme) => {
						const successColor = theme.palette.success.main;
						const errorColor = theme.palette.error.main;

						return {
							"& .MuiOutlinedInput-root": {
								fontWeight: 600,
								borderRadius: 2.5,
								backgroundColor: isWithinLimits
									? alpha(successColor, theme.palette.mode === "dark" ? 0.12 : 0.08)
									: alpha(errorColor, theme.palette.mode === "dark" ? 0.12 : 0.08),
								"& fieldset": {
									borderColor: isWithinLimits
										? alpha(successColor, 0.28)
										: alpha(errorColor, 0.28),
								},
							},
						};
					}}
					InputProps={{
						autoComplete: "off",
					}}
				/>
			</Grid>

			<Grid item xs={12} sm={5} md={3}>
				<TextField
					fullWidth
					placeholder={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.TagMeasurement.L206C18.AddDescription", "Add description")}
					variant="outlined"
					value={measurement.Comment || ""}
					size="small"
					onChange={(e) => {
						try {
							handleFieldChange(
								measurement.LabMeasurementId,
								measurement.LabAnalyzeId,
								"Comment",
								e.target.value,
							);
						} catch (error) {
							console.error("Error updating comment:", error);
						}
					}}
					sx={{
						"& .MuiOutlinedInput-root": {
							borderRadius: 2.5,
							bgcolor: "background.paper",
						},
					}}
				/>
			</Grid>
			<Grid item xs={12} sm={4} md={3}>
				<TextField
					label={
						<Translate String="LabDaily.Group.TestDateTime">
							Test Date & Time
						</Translate>
					}
					type="datetime-local"
					size="small"
					disabled={measurement?.AnalyseTime === 1}
					value={initialDate}
					onChange={(e) => {
						try {
							const value = e.target.value;
							const date = dayjs(value);
							if (!date.isValid()) {
								console.warn("Invalid date value:", value);
								return;
							}
							handleFieldChange(
								measurement.LabMeasurementId,
								measurement.LabAnalyzeId,
								"TestDate",
								date.toISOString(),
							);
							setInitialDate(value);
						} catch (error) {
							console.error("Error updating test date:", error);
						}
					}}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						readOnly: measurement.AnalyseType === 1, // Makes it non-editable
					}}
					fullWidth
					sx={{
						minWidth: 0,
						"& .MuiOutlinedInput-root": {
							borderRadius: 2.5,
							bgcolor: "background.paper",
						},
						"& .MuiInputBase-input": {
							padding: "8px 10px",
						},
					}}
				/>
			</Grid>

			<Grid item xs={12} sm={12} md={1}>
				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<Tooltip
						title={
							<Translate String="LabDaily.Group.MoreInfo">More info</Translate>
						}
					>
						<IconButton
							size="small"
							edge="end"
							aria-label={`Show measurement details for ${measurement.ParameterName}`}
							onClick={() => {
								try {
									handleDialogOpen(
										measurement.LabMeasurementId,
										measurement.LabAnalyzeId,
									);
								} catch (error) {
									console.error("Error opening dialog:", error);
								}
							}}
							sx={{
								color: "text.secondary",
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								bgcolor: "background.paper",
								"&:hover": {
									color: "primary.main",
									backgroundColor: "action.hover",
								},
							}}
						>
							<InfoIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Grid>
		</Grid>
	);
};

export default memo(
	TagMeasurement,
	(prev, next) =>
		prev.measurement === next.measurement &&
		prev.handleFieldChange === next.handleFieldChange &&
		prev.handleDialogOpen === next.handleDialogOpen,
);
